/**
 * Login page — cyberpunk themed authentication gate.
 * Full-page, no AppShell (no sidebar).
 */

import { useState, useEffect, useRef, type FormEvent } from "react";
import { useNavigate } from "@tanstack/react-router";
import { ScanlineOverlay } from "../components/ui/ScanlineOverlay";

export function LoginRoute() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [glitch, setGlitch] = useState(false);
  const usernameRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    usernameRef.current?.focus();
  }, []);

  // Glitch effect on error
  useEffect(() => {
    if (!error) return;
    setGlitch(true);
    const t = setTimeout(() => setGlitch(false), 600);
    return () => clearTimeout(t);
  }, [error]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const res = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ username, password }),
      });

      if (res.ok) {
        navigate({ to: "/chat", search: { agent: undefined, session: undefined } });
      } else {
        setError("ACCESS DENIED");
        setPassword("");
      }
    } catch {
      setError("CONNECTION FAILED");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{
        background: "var(--bw-bg, #0a0a0f)",
        color: "var(--bw-fg, #e4e4f0)",
        fontFamily: "ui-monospace, 'Cascadia Code', 'Fira Code', monospace",
      }}
    >
      <ScanlineOverlay intensity="low" />

      {/* Background grid */}
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(var(--bw-accent, #00f0ff) 1px, transparent 1px), linear-gradient(90deg, var(--bw-accent, #00f0ff) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
        }}
      />

      {/* Login card */}
      <div
        className={`
          relative z-10 w-full max-w-md mx-4 p-8
          border rounded-sm
          transition-all duration-200
          ${glitch ? "animate-glitch" : ""}
        `}
        style={{
          background: "var(--bw-surface, #0d0d14)",
          borderColor: error
            ? "var(--bw-danger, #ff003c)"
            : "var(--bw-border, #1a1a2e)",
          boxShadow: error
            ? "0 0 30px rgba(255, 0, 60, 0.3), inset 0 0 30px rgba(255, 0, 60, 0.05)"
            : "var(--bw-glow, 0 0 20px rgba(0, 240, 255, 0.1))",
        }}
      >
        {/* Logo + title */}
        <div className="flex items-center gap-4 mb-10">
          <div
            className="h-12 w-12 border-2 grid place-items-center flex-shrink-0"
            style={{
              borderColor: "var(--bw-accent, #00f0ff)",
              boxShadow: "var(--bw-glow-accent, 0 0 15px rgba(0, 240, 255, 0.4))",
            }}
          >
            <div
              className="h-5 w-5"
              style={{ background: "var(--bw-accent, #00f0ff)" }}
            />
          </div>
          <div>
            <h1
              className="text-2xl font-bold tracking-[0.2em] uppercase"
              style={{ color: "var(--bw-fg, #e4e4f0)" }}
            >
              BLACK WALL
            </h1>
            <p
              className="text-xs tracking-[0.3em] uppercase mt-1"
              style={{ color: "var(--bw-muted, #6a6a7e)" }}
            >
              SECURE TERMINAL
            </p>
          </div>
        </div>

        {/* Divider */}
        <div
          className="h-px mb-8 opacity-30"
          style={{ background: "var(--bw-accent, #00f0ff)" }}
        />

        {/* Error message */}
        {error && (
          <div
            className="mb-6 px-4 py-3 border rounded-sm text-sm tracking-wider text-center uppercase font-bold"
            style={{
              color: "var(--bw-danger, #ff003c)",
              borderColor: "var(--bw-danger, #ff003c)",
              background: "rgba(255, 0, 60, 0.05)",
              textShadow: "0 0 10px rgba(255, 0, 60, 0.5)",
            }}
          >
            ⚠ {error}
          </div>
        )}

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label
              className="block text-xs tracking-[0.2em] uppercase mb-2"
              style={{ color: "var(--bw-muted, #6a6a7e)" }}
            >
              OPERATOR ID
            </label>
            <input
              ref={usernameRef}
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              className="
                w-full px-4 py-3 text-sm
                border rounded-sm outline-none
                transition-all duration-200
                focus:shadow-[0_0_15px_rgba(0,240,255,0.2)]
                placeholder:tracking-wider
              "
              style={{
                background: "var(--bw-bg, #0a0a0f)",
                color: "var(--bw-fg, #e4e4f0)",
                borderColor: "var(--bw-border, #1a1a2e)",
                caretColor: "var(--bw-accent, #00f0ff)",
              }}
              placeholder="enter id..."
            />
          </div>

          <div>
            <label
              className="block text-xs tracking-[0.2em] uppercase mb-2"
              style={{ color: "var(--bw-muted, #6a6a7e)" }}
            >
              ACCESS KEY
            </label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              className="
                w-full px-4 py-3 text-sm
                border rounded-sm outline-none
                transition-all duration-200
                focus:shadow-[0_0_15px_rgba(0,240,255,0.2)]
                placeholder:tracking-wider
              "
              style={{
                background: "var(--bw-bg, #0a0a0f)",
                color: "var(--bw-fg, #e4e4f0)",
                borderColor: "var(--bw-border, #1a1a2e)",
                caretColor: "var(--bw-accent, #00f0ff)",
              }}
              placeholder="enter key..."
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="
              w-full py-3 mt-2
              text-sm font-bold tracking-[0.3em] uppercase
              border rounded-sm
              transition-all duration-200
              hover:shadow-[0_0_25px_rgba(0,240,255,0.4)]
              active:scale-[0.98]
              disabled:opacity-50 disabled:cursor-wait
            "
            style={{
              background: loading
                ? "var(--bw-surface, #0d0d14)"
                : "transparent",
              color: "var(--bw-accent, #00f0ff)",
              borderColor: "var(--bw-accent, #00f0ff)",
              textShadow: "0 0 10px rgba(0, 240, 255, 0.5)",
            }}
          >
            {loading ? "AUTHENTICATING..." : "ACCESS SYSTEM"}
          </button>
        </form>

        {/* Bottom decoration */}
        <div className="mt-8 flex items-center gap-3">
          <div
            className="flex-1 h-px opacity-20"
            style={{ background: "var(--bw-accent, #00f0ff)" }}
          />
          <span
            className="text-[10px] tracking-[0.4em] uppercase"
            style={{ color: "var(--bw-muted, #6a6a7e)" }}
          >
            SINGLE OPERATOR
          </span>
          <div
            className="flex-1 h-px opacity-20"
            style={{ background: "var(--bw-accent, #00f0ff)" }}
          />
        </div>
      </div>

      {/* Glitch keyframes */}
      <style>{`
        @keyframes glitch {
          0% { transform: translate(0); }
          20% { transform: translate(-2px, 1px); }
          40% { transform: translate(2px, -1px); }
          60% { transform: translate(-1px, -2px); }
          80% { transform: translate(1px, 2px); }
          100% { transform: translate(0); }
        }
        .animate-glitch {
          animation: glitch 0.3s ease-in-out 2;
        }
      `}</style>
    </div>
  );
}
